import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS, API_BASE_URL } from "../../constants/api-urls";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import "./AuctionDetail.scss";
import LinkedLogisticsPanel from "../../components/moduleLogistics/LinkedLogisticsPanel";
import { isAuctionListingLive } from "../../utils/auctionListingApproval";
import {
  getBuyerStartingPrice,
  getBuyerHighestBidAmount,
  getBuyerMinNextBid,
  isBuyerAuctionReserveMet,
} from "../../utils/auctionPricing";

const API_BASE_URL_MEDIA = API_BASE_URL.replace("/api/v1", "");

export default function AuctionDetail() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [relatedAuctions, setRelatedAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const [bidAmount, setBidAmount] = useState("");
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  
  // Get master data for ID to label mapping
  const { 
    attributeUnitOptions, 
    dimensionUnitOptions,
    packagingMaterialsOptions,
    labellingOptions,
    inventoryStatusOptions,
    customizationTypesOptions
  } = useProductDetailsMaster();

  // Helper function to get full image/video URL from access path
  const getMediaUrl = (accessPath) => {
    if (!accessPath) return '';
    
    // If already a full URL, return as is
    if (accessPath.startsWith('http://') || accessPath.startsWith('https://')) {
      return accessPath;
    }
    
    const baseUrl = API_BASE_URL_MEDIA;
    
    // Handle old path formats that might have Windows absolute paths
    if (accessPath.includes('C:/usr/local/b2b/seller/AuctionProduct') || accessPath.includes('C:\\usr\\local\\b2b\\seller\\AuctionProduct')) {
      // Extract auction ID and filename from path format
      const match = accessPath.match(/AuctionProduct[\/\\](\d+)[\/\\]([^\/\\]+)$/);
      if (match) {
        const extractedAuctionId = match[1];
        const fileName = match[2];
        return `${baseUrl}/auction-media/${extractedAuctionId}/${fileName}`;
      }
      // Handle temp folder
      const tempMatch = accessPath.match(/AuctionProduct[\/\\]temp[\/\\]([^\/\\]+)$/);
      if (tempMatch) {
        const fileName = tempMatch[1];
        return `${baseUrl}/auction-media/temp/${fileName}`;
      }
    }
    
    // If it's already a relative path starting with /auction-media, use it directly
    if (accessPath.startsWith('/auction-media/')) {
      return `${baseUrl}${accessPath}`;
    }
    
    // If relative path, prepend base URL
    let cleanPath = accessPath;
    if (cleanPath.startsWith(':3000')) {
      cleanPath = cleanPath.replace(/^:3000/, '');
    }
    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
    
    // If path doesn't start with /auction-media, assume it's for the current auction
    if (!cleanPath.startsWith('/auction-media')) {
      // Try to extract filename from path
      const fileName = cleanPath.split('/').pop();
      if (fileName && auctionId) {
        return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
      }
    }
    
    return `${baseUrl}${cleanPath}`;
  };

  useEffect(() => {
    fetchAuction();
    fetchBids();
    fetchRelatedAuctions();
  }, [auctionId]);

  const fetchAuction = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      
      if (response && response.success && response.data) {
        console.log("Auction data:", response.data);
        console.log("Category ID:", response.data.category_id);
        console.log("Product attributes:", response.data.product_attributes);
        console.log("Product attributes type:", typeof response.data.product_attributes);
        if (response.data.product_attributes) {
          console.log("Product attributes keys:", Object.keys(response.data.product_attributes));
        }
        setAuction(response.data);
      }
    } catch (error) {
      console.error("Error fetching auction:", error);
      toast.error("Failed to fetch auction details");
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
      
      if (response && response.success && response.data) {
        setBids(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching bids:", error);
    }
  };

  const fetchRelatedAuctions = async () => {
    try {
      const response = await CommonService.get(API_URLS.AUCTION.ACTIVE);
      if (response && response.success && response.data) {
        // Filter out current auction and get related auctions (same category or limit to 3)
        const related = Array.isArray(response.data) 
          ? response.data
              .filter(a => a.auction_id !== parseInt(auctionId))
              .slice(0, 3)
          : [];
        setRelatedAuctions(related);
      }
    } catch (error) {
      console.error("Error fetching related auctions:", error);
    }
  };

  const formatCurrency = (amount) => {
    // Handle NaN, null, undefined, or invalid numbers
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return "0d 0h";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return `${days}d ${hours}h`;
  };

  const calculateMinBid = () => {
    if (!auction) return 0;
    const highestBid = bids.find((b) => b.is_highest_bid === "Y");
    return getBuyerMinNextBid(auction, highestBid || null);
  };

  // Check if user is a buyer (buyer role or both role)
  const isBuyer = () => {
    const user = StorageService.getUser();
    if (!user) return false;
    
    // Check user_type_id: 1 = BUYER, 2 = SELLER, 3 = BOTH
    const userTypeId = String(user.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    // Check if user_type_id includes '1' (Buyer) or '3' (Both)
    const isBuyerByUserType = typeIds.includes('1') || 
                              typeIds.includes('3') || 
                              userTypeId === '1' || 
                              userTypeId === '3';
    
    // Also check company_role from user object
    const companyRole = user.company_role || '';
    const isBuyerByRole = companyRole === 'BUYER' || companyRole === 'BOTH';
    
    return isBuyerByUserType || isBuyerByRole;
  };

  const isAuctionCreator = () => {
    const user = StorageService.getUser();
    if (!user || auction?.user_login_id == null) return false;
    const viewerId = Number(user.user_login_id ?? user.id);
    const ownerId = Number(auction.user_login_id);
    return Number.isFinite(viewerId) && Number.isFinite(ownerId) && viewerId === ownerId;
  };

  const handlePlaceBid = async () => {
    const minBid = calculateMinBid();
    const bidValue = parseFloat(bidAmount);
    
    if (!bidValue || bidValue < minBid) {
      toast.error(`Minimum bid amount is ${formatCurrency(minBid)}`);
      return;
    }

    try {
      const response = await CommonService.post(API_URLS.AUCTION.PLACE_BID, {
        auction_id: parseInt(auctionId),
        bid_amount: bidValue,
      });

      if (response && response.success) {
        // Navigate to bid success page with auction and bid data
        navigate(`/auction/${auctionId}/bid/success`, {
          state: {
            auction: auction,
            bidAmount: bidValue,
            bidId: response.data?.bidId || response.data?.bid_id
          }
        });
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to place bid";
      toast.error(errorMessage);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getUOMLabel = (uom) => {
    if (!uom) return '';
    // Try to find in attributeUnitOptions first
    const uomOption = attributeUnitOptions.find(u => 
      String(u.value) === String(uom) || 
      Number(u.value) === Number(uom)
    );
    if (uomOption) return uomOption.label;
    
    // Fallback to common unit labels mapping
    const unitMap = {
      '1': 'Pieces',
      '2': 'Kilograms',
      '3': 'Grams',
      '4': 'Tons',
      '5': 'Liters',
      '6': 'Milliliters',
      '7': 'Meters',
      '8': 'Centimeters',
      '9': 'Square Meters',
      '10': 'Cubic Meters',
      '11': 'Pieces',
      '12': 'Units',
    };
    return unitMap[String(uom)] || uom;
  };

  // Function to map attribute value (ID) to label based on attribute key
  const getAttributeLabel = (key, value) => {
    if (!value || value === null || value === undefined || value === '') {
      return value;
    }

    const valueStr = String(value);
    
    // Map based on attribute key patterns
    const keyLower = key.toLowerCase();
    
    // Unit of measure related
    if (keyLower.includes('unit') || keyLower.includes('uom') || keyLower.includes('measure')) {
      const option = attributeUnitOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // Dimension unit related
    if (keyLower.includes('dimension') || keyLower.includes('dimention')) {
      const option = dimensionUnitOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // Packaging materials
    if (keyLower.includes('packaging') || keyLower.includes('packing') || keyLower.includes('material')) {
      const option = packagingMaterialsOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // Labelling
    if (keyLower.includes('labelling') || keyLower.includes('label')) {
      const option = labellingOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // Inventory status
    if (keyLower.includes('inventory') || keyLower.includes('status')) {
      const option = inventoryStatusOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // Customization types
    if (keyLower.includes('customization') || keyLower.includes('custom')) {
      const option = customizationTypesOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      return option ? option.label : value;
    }
    
    // If value looks like a number (ID), try to find in all options
    if (!isNaN(value) && value !== '') {
      // Try all option arrays
      const allOptions = [
        ...attributeUnitOptions,
        ...dimensionUnitOptions,
        ...packagingMaterialsOptions,
        ...labellingOptions,
        ...inventoryStatusOptions,
        ...customizationTypesOptions
      ];
      
      const foundOption = allOptions.find(opt => 
        String(opt.value) === valueStr || Number(opt.value) === Number(value)
      );
      
      if (foundOption) {
        return foundOption.label;
      }
    }
    
    // Return original value if no mapping found
    return value;
  };

  if (loading) {
    return <div className="loading">Loading auction details...</div>;
  }

  if (!auction) {
    return <div className="error">Auction not found</div>;
  }

  const highestBid = bids.find(b => b.is_highest_bid === 'Y');
  const minBid = calculateMinBid();
  const images = auction.images && Array.isArray(auction.images) ? auction.images : [];
  const currentImage = images[selectedImageIndex] || images[0];

  const viewerIsCreator = isAuctionCreator();
  const reservePrice = viewerIsCreator ? parseFloat(auction.reserve_price) || 0 : 0;
  const currentBidAmount = highestBid?.bid_amount
    ? parseFloat(highestBid.bid_amount)
    : getBuyerHighestBidAmount(auction) ?? 0;
  const reserveMet = viewerIsCreator
    ? auction.reserve_price_reached === "Y" ||
      auction.reserve_price_reached === "y" ||
      (currentBidAmount >= reservePrice && reservePrice > 0)
    : isBuyerAuctionReserveMet(auction);
  const endDateReached = new Date(auction.end_date) <= new Date();
  const isEndOnScheduledOnly = auction.auction_end_condition === 'End on Scheduled Date Only';
  const isEndWhenReserveMet = auction.auction_end_condition === 'End When Reserve Met';
  const listingLive = isAuctionListingLive(auction);
  const isPendingListingApproval =
    auction.status === "pending_approval" || auction.listing_approval_status === "pending";
  const isRejectedListing =
    auction.status === "rejected" || auction.listing_approval_status === "rejected";
  const showPlaceBidNow =
    listingLive &&
    isBuyer() &&
    !viewerIsCreator &&
    !(auction.seller_decision === 'accepted' && auction.winning_bid_id) &&
    !(isEndWhenReserveMet && reserveMet) &&
    !(isEndOnScheduledOnly && endDateReached);
  const showWatchlist = !viewerIsCreator;

  return (
    <div className="auction-detail-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>

        {isPendingListingApproval && (
          <div className="auction-listing-approval-banner auction-listing-approval-banner--pending" role="status">
            <strong>Pending listing approval</strong>
            <p>
              This auction is not live on the marketplace yet. Bidding will open after your
              organization completes the approval workflow in Procure-to-Pay.
            </p>
          </div>
        )}
        {isRejectedListing && (
          <div className="auction-listing-approval-banner auction-listing-approval-banner--rejected" role="status">
            <strong>Listing approval rejected</strong>
            <p>
              This auction was not approved for listing. Update the draft from My Auctions or
              contact your approvers.
            </p>
          </div>
        )}

        {auctionId && (
          <LinkedLogisticsPanel
            sourceModule="auction"
            sourceRecordType="auction_lot"
            sourceRecordId={String(auctionId)}
            linkPurpose="fulfillment"
            title="Auction logistics"
          />
        )}

        <div className="main-content-layout">
          {/* Left Column */}
          <div className="left-column">
            {/* Product Image Card */}
            <div className="image-card">
              <div className="auction-images">
                {images.length > 0 ? (
                  <>
                    <div className="image-thumbnails-wrapper">
                      {images.length > 4 && (
                        <button 
                          className="thumbnail-nav-btn thumbnail-nav-up"
                          onClick={() => {
                            const thumbnails = document.querySelector('.image-thumbnails');
                            if (thumbnails) {
                              thumbnails.scrollBy({ top: -100, behavior: 'smooth' });
                            }
                          }}
                        >
                          ↑
                        </button>
                      )}
                      <div className="image-thumbnails">
                        {images.map((img, index) => (
                          <div
                            key={index}
                            className={`thumbnail ${selectedImageIndex === index ? 'active' : ''}`}
                            onClick={() => setSelectedImageIndex(index)}
                          >
                            <img
                              src={getMediaUrl(img)}
                              alt={`${auction.product_name} - ${index + 1}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      {images.length > 4 && (
                        <button 
                          className="thumbnail-nav-btn thumbnail-nav-down"
                          onClick={() => {
                            const thumbnails = document.querySelector('.image-thumbnails');
                            if (thumbnails) {
                              thumbnails.scrollBy({ top: 100, behavior: 'smooth' });
                            }
                          }}
                        >
                          ↓
                        </button>
                      )}
                    </div>
                    <div className="main-image-wrapper">
                      <div className="main-image">
                        {selectedImageIndex > 0 && (
                          <button 
                            className="image-nav-btn image-nav-left"
                            onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
                          >
                            ‹
                          </button>
                        )}
                        <img
                          src={getMediaUrl(currentImage)}
                          alt={auction.product_name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="placeholder-image hidden">No Image Available</div>
                        {selectedImageIndex < images.length - 1 && (
                          <button 
                            className="image-nav-btn image-nav-right"
                            onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
                          >
                            ›
                          </button>
                        )}
                      </div>
                      {images.length > 1 && (
                        <div className="small-preview-image">
                          <img
                            src={getMediaUrl(images[selectedImageIndex === images.length - 1 ? 0 : selectedImageIndex + 1])}
                            alt="Next preview"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="main-image">
                    <div className="placeholder-image">No Image Available</div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs Card */}
            <div className="tabs-card">
              <div className="tabs">
                <button
                  className={activeTab === "description" ? "active" : ""}
                  onClick={() => setActiveTab("description")}
                >
                  Product Description
                </button>
                <button
                  className={activeTab === "attributes" ? "active" : ""}
                  onClick={() => setActiveTab("attributes")}
                >
                  Attributes
                </button>
                <button
                  className={activeTab === "pickup" ? "active" : ""}
                  onClick={() => setActiveTab("pickup")}
                >
                  Pick-up
                </button>
                <button
                  className={activeTab === "taxes" ? "active" : ""}
                  onClick={() => setActiveTab("taxes")}
                >
                  Taxes
                </button>
              </div>

              <div className="tab-content">
                {activeTab === "description" && (
                  <div>
                    <p className="description-text">
                      {auction.product_description || "No description available"}
                    </p>
                  </div>
                )}

                {activeTab === "attributes" && (
                  <div className="attributes-section">
                    <h3 className="attributes-title">Attributes</h3>
                    {auction.product_attributes && 
                     typeof auction.product_attributes === 'object' &&
                     Object.keys(auction.product_attributes).length > 0 ? (
                      <div className="attributes-grid">
                        {(() => {
                          // First, collect all attribute entries
                          const allAttributes = Object.entries(auction.product_attributes);
                          
                          // Category prefixes that might be used in base product_attributes
                          const categoryPrefixes = [
                            'construction_', 'vehicle_', 'packaging_', 'energy_electrical_',
                            'construction_materials_', 'event_exhibition_', 'scrap_recyclables_',
                            'furniture_office_', 'healthcare_supplies_', 'livestock_dairy_',
                            'retail_fmcg_', 'real_estate_leasing_', 'it_electronics_', 'medical_laboratory_'
                          ];
                          
                          // Create a set of non-prefixed keys that exist (category-specific attributes)
                          const nonPrefixedKeys = new Set();
                          allAttributes.forEach(([key]) => {
                            // Check if this key doesn't start with any category prefix
                            const hasPrefix = categoryPrefixes.some(prefix => key.toLowerCase().startsWith(prefix));
                            if (!hasPrefix) {
                              nonPrefixedKeys.add(key.toLowerCase());
                            }
                          });
                          
                          // Filter attributes: remove prefixed keys if non-prefixed version exists
                          const filteredAttributes = allAttributes.filter(([key, value]) => {
                            // Filter out empty values, null, undefined, and empty strings
                            // Also filter out internal/system keys that shouldn't be displayed
                            const systemKeys = ['auction_id', 'vehicle_attribute_id', 'construction_attribute_id', 
                                              'packaging_attribute_id', 'created_at', 'updated_at'];
                            if (systemKeys.includes(key.toLowerCase())) {
                              return false;
                            }
                            
                            // Check if value is empty
                            if (value === null || 
                                value === undefined || 
                                value === '' || 
                                (Array.isArray(value) && value.length === 0) ||
                                (typeof value === 'object' && value !== null && Object.keys(value).length === 0)) {
                              return false;
                            }
                            
                            // Check if this key has a category prefix
                            const matchingPrefix = categoryPrefixes.find(prefix => 
                              key.toLowerCase().startsWith(prefix)
                            );
                            
                            if (matchingPrefix) {
                              // This is a prefixed key, check if non-prefixed version exists
                              const keyWithoutPrefix = key.substring(matchingPrefix.length);
                              if (nonPrefixedKeys.has(keyWithoutPrefix.toLowerCase())) {
                                // Non-prefixed version exists, filter out this prefixed one
                                return false;
                              }
                            }
                            
                            return true;
                          });
                          
                          return filteredAttributes
                            .sort(([keyA], [keyB]) => {
                              // Sort attributes alphabetically
                              return keyA.localeCompare(keyB);
                            })
                            .map(([key, value]) => {
                              // Remove category prefix from key for display
                              let displayKey = key;
                              const matchingPrefix = categoryPrefixes.find(prefix => 
                                key.toLowerCase().startsWith(prefix)
                              );
                              if (matchingPrefix) {
                                displayKey = key.substring(matchingPrefix.length);
                              }
                              
                              // Format the value for display - map IDs to labels
                              let displayValue = value;
                              if (Array.isArray(value)) {
                                // Map each array item if it's an ID
                                displayValue = value.map(item => getAttributeLabel(displayKey, item)).join(', ');
                              } else if (typeof value === 'object' && value !== null) {
                                // Handle JSON objects - try to format nicely
                                if (Array.isArray(value)) {
                                  displayValue = value.join(', ');
                                } else {
                                  displayValue = JSON.stringify(value);
                                }
                              } else {
                                // Map ID to label for single values (use displayKey, not original key)
                                displayValue = getAttributeLabel(displayKey, value);
                              }
                              
                              // Format the key (convert snake_case to Title Case with better formatting)
                              const formattedKey = displayKey
                                .split('_')
                                .map((word, index) => {
                                  // Handle special abbreviations
                                  if (word.toUpperCase() === 'ID' || word.toUpperCase() === 'VIN' || 
                                      word.toUpperCase() === 'EV' || word.toUpperCase() === 'HP' ||
                                      word.toUpperCase() === 'CGST' || word.toUpperCase() === 'SGST' ||
                                      word.toUpperCase() === 'IGST' || word.toUpperCase() === 'GST' ||
                                      word.toUpperCase() === 'HSN' || word.toUpperCase() === 'HS' ||
                                      word.toUpperCase() === 'UNSPSC' || word.toUpperCase() === 'VAT') {
                                    return word.toUpperCase();
                                  }
                                  // Capitalize first letter, rest lowercase
                                  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                                })
                                .join(' ');
                              
                              return (
                                <div key={key} className="attribute-item">
                                  <span className="attr-key">{formattedKey}</span>
                                  <span className="attr-value">{displayValue}</span>
                                </div>
                              );
                            });
                        })()}
                      </div>
                    ) : (
                      <div className="no-attributes">
                        <p>No attributes available for this product.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "pickup" && (
                  <div className="pickup-section">
                    <h3 className="pickup-title">Pickup Details</h3>
                    <div className="pickup-details-grid">
                      <div className="pickup-item">
                        <span className="pickup-label">Loading Responsibility</span>
                        <span className="pickup-value">{auction.loading_responsibility || "N/A"}</span>
                      </div>
                      <div className="pickup-item">
                        <span className="pickup-label">Packaging Responsibility</span>
                        <span className="pickup-value">{auction.packaging_responsibility || "N/A"}</span>
                      </div>
                      <div className="pickup-item">
                        <span className="pickup-label">Insurance</span>
                        <span className="pickup-value">{auction.insurance || "N/A"}</span>
                      </div>
                      <div className="pickup-item">
                        <span className="pickup-label">Nearest Port</span>
                        <span className="pickup-value">
                          {auction.product_attributes?.nearest_port || 
                           auction.product_attributes?.nearestPort || 
                           "NA"}
                        </span>
                      </div>
                      <div className="pickup-item">
                        <span className="pickup-label">Dispatch Location</span>
                        <span className="pickup-value">
                          {auction.product_attributes?.dispatch_location || 
                           auction.product_attributes?.dispatchLocation ||
                           (auction.seller_company || auction.seller_name || "") + 
                           (auction.city ? ` - ${auction.city}` : "") + 
                           (auction.state ? `, ${auction.state}` : "") || 
                           "N/A"}
                        </span>
                      </div>
                      <div className="pickup-item">
                        <span className="pickup-label">Export Under</span>
                        <span className="pickup-value">
                          {auction.is_export === 'Yes' 
                            ? (auction.incoterms || "WIP, LUT") 
                            : "NA"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "taxes" && (
                  <div className="taxes-section">
                    <h3 className="taxes-title">Taxes</h3>
                    <div className="taxes-grid">
                      <div className="taxes-column">
                        <div className="tax-item">
                          <span className="tax-label">HSN Code</span>
                          <span className="tax-value">{auction.hsn_code || "N/A"}</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">UNSPSC Code</span>
                          <span className="tax-value">{auction.unspsc_code || "N/A"}</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">CGST%</span>
                          <span className="tax-value">{auction.cgst_rate || 0}%</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">HS Code</span>
                          <span className="tax-value">{auction.hs_code || "N/A"}</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">VAT%</span>
                          <span className="tax-value">
                            {auction.product_attributes?.vat_rate || 
                             auction.product_attributes?.vat || 
                             "N/A"}
                          </span>
                        </div>
                      </div>
                      <div className="taxes-column">
                        <div className="tax-item">
                          <span className="tax-label">GSTIN</span>
                          <span className="tax-value">{auction.gst_rate || 0}%</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">IGST%</span>
                          <span className="tax-value">{auction.igst_rate || 0}%</span>
                        </div>
                        <div className="tax-item">
                          <span className="tax-label">SGST%</span>
                          <span className="tax-value">{auction.sgst_rate || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* FAQ Card */}
            {auction.faqs && auction.faqs.length > 0 && (
              <div className="faq-card">
                <h3>Frequently Asked Questions</h3>
                {auction.faqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <div
                      className="faq-question"
                      onClick={() => toggleFaq(index)}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-icon">{expandedFaqs[index] ? "−" : "+"}</span>
                    </div>
                    {expandedFaqs[index] && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Bidding Information Card */}
          <div className="right-column">
            <div className="bidding-card">
              <h1 className="product-title">{auction.product_name}</h1>
              <div className="condition-tag">Excellent</div>

              <div className="base-price-section">
                <span className="base-price-label">Starting Price</span>
                <span className="base-price-value">
                  {formatCurrency(getBuyerStartingPrice(auction))} per unit
                </span>
              </div>

              <div className="product-details-section">
                <h3 className="product-details-title">Product Details</h3>
                <div className="product-details-list">
                  <div className="detail-item">
                    <span className="detail-label">Brand:</span>
                    <span className="detail-value">{auction.product_attributes?.brand || auction.product_name?.split(' ')[0] || "N/A"}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">
                      {[auction.country, auction.seller_company || auction.seller_name, auction.city]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Incremental Bidding:</span>
                    <span className="detail-value">{formatCurrency(auction.bid_increment)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Auctioned Unit:</span>
                    <span className="detail-value">
                      {auction.quantity || 1} {getUOMLabel(auction.unit_of_measure)}
                    </span>
                  </div>
                  {viewerIsCreator ? (
                    <div className="detail-item">
                      <span className="detail-label">Reserve Type:</span>
                      <span className="detail-value">{auction.reserve_type || "Reserve Price Set"}</span>
                    </div>
                  ) : null}
                  <div className="detail-item">
                    <span className="detail-label">Single Item Lot:</span>
                    <span className="detail-value">{auction.product_name}</span>
                  </div>
                </div>
              </div>

              <div className="highest-bid-section">
                <div className="highest-bid-header">
                  <span className="highest-bid-icon">{listingLive ? "✓" : "○"}</span>
                  <span className="highest-bid-label">Highest Bid</span>
                </div>
                <div className="highest-bid-value">
                  {highestBid?.bid_amount || getBuyerHighestBidAmount(auction) != null
                    ? formatCurrency(
                        highestBid?.bid_amount ||
                          getBuyerHighestBidAmount(auction)
                      )
                    : "No bids yet"}
                </div>
                <div className="bid-count">
                  {listingLive
                    ? `${bids.length} bid(s) placed`
                    : isPendingListingApproval
                      ? "Not open for bidding — pending approval"
                      : "Not open for bidding"}
                </div>
              </div>

              {listingLive && (
              <div className="time-remaining-section">
                <span className="time-icon">🕐</span>
                <span className="time-label">Time Remaining</span>
                <span className="time-value">{getTimeRemaining(auction.end_date)}</span>
              </div>
              )}

              <div className="auction-actions">
                {showPlaceBidNow && (
                  <button
                    className="btn-primary"
                    onClick={() => {
                      // Reset bid amount and recalculate min bid when opening modal
                      setBidAmount("");
                      setShowBidModal(true);
                    }}
                  >
                    Place Bid Now
                  </button>
                )}
                {isBuyer() && !viewerIsCreator && !(auction.seller_decision === 'accepted' && auction.winning_bid_id) && isEndWhenReserveMet && reserveMet && (
                  <div className="reserve-price-met-message" style={{ padding: '10px', textAlign: 'center', color: '#0ea5e9', fontWeight: '600', fontSize: '14px' }}>
                    Minimum sale threshold reached
                  </div>
                )}
                {isBuyer() && !viewerIsCreator && !(auction.seller_decision === 'accepted' && auction.winning_bid_id) && isEndOnScheduledOnly && endDateReached && (
                  <div className="auction-ended-message" style={{ padding: '10px', textAlign: 'center', color: '#64748b', fontWeight: '500', fontSize: '14px' }}>
                    Auction ended
                  </div>
                )}
                {showWatchlist ? (
                  <button type="button" className="btn-secondary">
                    Add to Watchlist
                  </button>
                ) : null}
              </div>

              {/* Auction Details Box */}
              <div className="auction-details-box">
                <h3>Auction Details</h3>
                <div className="detail-row">
                  <span className="detail-label">Auction Start Date:</span>
                  <span className="detail-value">{formatDateTime(auction.start_date)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Auction End Date:</span>
                  <span className="detail-value">{formatDateTime(auction.end_date)}</span>
                </div>
                {auction.inspection_start_date && (
                  <div className="detail-row">
                    <span className="detail-label">Inspection Date:</span>
                    <span className="detail-value">{formatDate(auction.inspection_start_date)}</span>
                  </div>
                )}
                {auction.pickup_window_days && (
                  <div className="detail-row">
                    <span className="detail-label">Pickup Window:</span>
                    <span className="detail-value">
                      {(() => {
                        const startDate = new Date(auction.end_date);
                        startDate.setDate(startDate.getDate() + 1);
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + (auction.pickup_window_days || 7));
                        return `${formatDate(startDate.toISOString())} - ${formatDate(endDate.toISOString())}`;
                      })()}
                    </span>
                  </div>
                )}
                {auction.inspection_start_time && (
                  <div className="detail-row">
                    <span className="detail-label">Inspection Start Time:</span>
                    <span className="detail-value">{auction.inspection_start_time}</span>
                  </div>
                )}
                {auction.inspection_end_time && (
                  <div className="detail-row">
                    <span className="detail-label">Inspection End Time:</span>
                    <span className="detail-value">{auction.inspection_end_time}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Related Auctions */}
        {relatedAuctions.length > 0 && (
          <div className="related-auctions">
            <h2>Related Auction</h2>
            <div className="related-auctions-grid">
              {relatedAuctions.map((relatedAuction) => {
                const relatedStartingPrice = getBuyerStartingPrice(relatedAuction);
                const relatedHighestBid = getBuyerHighestBidAmount(relatedAuction);
                const relatedHasBids = relatedHighestBid != null;
                const relatedImages = relatedAuction.images && Array.isArray(relatedAuction.images) ? relatedAuction.images : [];
                return (
                  <div key={relatedAuction.auction_id} className="related-auction-card">
                    <div className="related-image">
                      {relatedImages.length > 0 ? (
                        <img
                          src={getMediaUrl(relatedImages[0])}
                          alt={relatedAuction.product_name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="placeholder-image">No Image</div>
                      )}
                    </div>
                    <div className="related-info">
                      <h3>{relatedAuction.product_name}</h3>
                      <div className="related-price">
                        <div className="price-row">
                          <span className="price-label">Starting Price</span>
                          <span className="price-value starting-price">
                            {formatCurrency(relatedStartingPrice)}
                          </span>
                        </div>
                        {relatedHasBids && (
                          <div className="price-row">
                            <span className="price-label">Current Bid</span>
                            <span className="price-value current-bid">{formatCurrency(relatedHighestBid)}</span>
                          </div>
                        )}
                      </div>
                      <div className="related-location">
                        <span className="location-icon">📍</span>
                        <span className="location-text">
                          {relatedAuction.seller_company || relatedAuction.seller_name || "N/A"} - {relatedAuction.city || ""}
                        </span>
                      </div>
                      <Link
                        to={`/auction/${relatedAuction.auction_id}`}
                        className="view-auction-btn"
                      >
                        View Auction
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bid Modal */}
      {showBidModal && (() => {
        // Recalculate minBid when modal is shown to ensure fresh values
        const currentMinBid = calculateMinBid();
        const currentHighestBid = bids.find(b => b.is_highest_bid === 'Y');
        const currentHighestBidAmount = currentHighestBid?.bid_amount
          ? parseFloat(currentHighestBid.bid_amount)
          : getBuyerHighestBidAmount(auction) ?? 0;
        
        return (
          <div className="modal-overlay" onClick={() => setShowBidModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Place Your Bid</h2>
              <div className="bid-info">
                <div className="info-row">
                  <span className="label">Current Highest Bid:</span>
                  <span className="value">
                    {currentHighestBid?.bid_amount || getBuyerHighestBidAmount(auction) != null
                      ? formatCurrency(currentHighestBidAmount)
                      : "No bids yet"}
                  </span>
                </div>
                <div className="info-row highlight">
                  <span className="label">Minimum Next Bid:</span>
                  <span className="value">{formatCurrency(currentMinBid)}</span>
                </div>
              </div>
              <div className="bid-input">
                <label>Your Bid Amount (₹)</label>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder={`Minimum ${formatCurrency(currentMinBid)}`}
                  min={currentMinBid || 0}
                  step="any"
                />
              </div>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  onClick={() => setShowBidModal(false)}
                >
                  Cancel
                </button>
                <button className="btn-primary" onClick={handlePlaceBid}>
                  Place Bid
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
